import { useCallback } from 'react'

import { isAxiosError } from 'axios'
import useSWRMutation from 'swr/mutation'
import { z } from 'zod'

import {
  reservationApi,
  tokenOptions,
  transactionApi,
} from '../../apis/suitebookClient'
import { runBotVerificationDialog } from '../../components/contents/BotVerificationDialog'

const verificationSchema = z.discriminatedUnion('enabled', [
  z.object({ enabled: z.literal(false) }),
  z.object({
    enabled: z.literal(true),
    siteKey: z.string().min(1),
    action: z.string().min(1),
    context: z.string().min(1),
  }),
])

// 30 Seconds
const TIMEOUT_MSEC = 30000

export const useCreateReservationApi = (
  token: string,
  transactionId: number
) => {
  const options = tokenOptions(token)

  const createReservation = useCallback(async () => {
    const getHeaders = async (): Promise<Record<string, string>> => {
      const response = await transactionApi
        .getPaymentBotVerification(transactionId, {
          ...options,
          timeout: TIMEOUT_MSEC,
        })
        .catch((error: unknown) => {
          if (isAxiosError(error) && error.code === 'ECONNABORTED') {
            // No reservation was submitted; keep the confirmation screen retryable.
            throw new Error('Security verification configuration timed out', {
              cause: error,
            })
          }
          throw error
        })
      const configuration = verificationSchema.parse(response.data)
      if (!configuration.enabled) return options.headers
      return {
        ...options.headers,
        'X-Captcha-Token': await runBotVerificationDialog(configuration),
      }
    }
    const submit = (headers: Record<string, string>) =>
      reservationApi
        .createReservation(
          { transactionId },
          { ...options, headers, timeout: TIMEOUT_MSEC }
        )
        .then((response) => ({ data: response.data, status: response.status }))
    const headers = await getHeaders()
    try {
      return await submit(headers)
    } catch (error) {
      if (
        headers['X-Captcha-Token'] ||
        !isAxiosError(error) ||
        error.response?.status !== 403 ||
        !z
          .object({ code: z.literal('booking_bot_verification_required') })
          .safeParse(error.response.data).success
      )
        throw error
      // Only the server's pre-authorization challenge response permits one retry.
      return submit(await getHeaders())
    }
  }, [options, transactionId])

  const { trigger, error, data } = useSWRMutation(
    '/reservation/create',
    createReservation
  )

  return {
    trigger,
    error,
    data,
  }
}
